import React, { useCallback } from 'react'

import { useDrop, useDrag } from '@sketch/modules-common'

import { useToast } from '@sketch/toasts'

import { useTransferStorageItem } from '../../storageItems'
import { ErrorHandler } from '@sketch/tracing'
import { MovedToProjectToast } from '../../storageItems/components'

interface UseNestedProjectDropSharesProps {
  projectIdentifier: string
  workspaceId: string
  disabled?: boolean
}

/**
 * Creates a droppable area for nested projects, accepts dragging both shares
 * or projects.
 */
export const useNestedProjectDropShares = ({
  projectIdentifier,
  workspaceId,
  disabled = false,
}: UseNestedProjectDropSharesProps) => {
  const { showToast } = useToast()

  const [transferItemRaw] = useTransferStorageItem({
    onError: errorMessage => {
      showToast(errorMessage, 'negative')
    },
  })

  const transferItem = useCallback(
    ({
      type,
      projectId,
      identifier,
      oldProjectId,
    }: {
      type: 'Share' | 'Project'
      projectId: string
      identifier: string
      oldProjectId: string | undefined
    }) => {
      transferItemRaw({
        onCompleted: data => {
          const storageItem = data.transferStorageItem.storageItem
          const newProject =
            storageItem.__typename === 'Share'
              ? storageItem.project
              : storageItem.parentProjects.find(
                  x => x.identifier === storageItem.parentProjectIdentifier
                )

          if (!newProject) {
            ErrorHandler.shouldNeverHappen('newProject is not defined')
            return
          }

          showToast(
            <MovedToProjectToast
              project={newProject}
              type={storageItem.__typename === 'Share' ? 'Document' : 'Folder'}
              workspaceId={workspaceId}
            />
          )
        },
        item: {
          __typename: type,
          identifier: identifier,
        },
        location: {
          source: { workspaceId, projectId: oldProjectId },
          destination: { workspaceId, projectId },
        },
      })
    },
    [showToast, transferItemRaw, workspaceId]
  )

  const dragEvents = useDrag({
    type: 'project',
    identifier: projectIdentifier,
  })

  const dropEvents = useDrop({
    onShareDropped: ({ shareId, share }) => {
      if (!share) {
        ErrorHandler.shouldNeverHappen('share is not defined')
        return
      }

      transferItem({
        type: 'Share',
        projectId: projectIdentifier,
        identifier: shareId,
        oldProjectId: share?.project?.identifier,
      })
    },
    onProjectDropped: ({ projectId, project }) => {
      if (!project) {
        ErrorHandler.shouldNeverHappen('project is not defined')
        return
      }

      transferItem({
        type: 'Project',
        projectId: projectIdentifier,
        identifier: projectId,
        oldProjectId: project?.parentProjectIdentifier ?? undefined,
      })
    },
  })

  return {
    draggable: !disabled,
    ...dragEvents,
    ...dropEvents,
  }
}
